import { I18n } from '../utils/lang';
import { ERROR_TYPE } from '../constants/error';
import { ENV } from '../constants/net';
import { onErrorSubject } from '../lib/subject';
import store from '../store/cache';
import { clearParams, getApiUrl } from './service';
import { HttpError } from './userAuth/interface';

export interface EnhancedError extends Error {
  response?: any;
  data?: any;
}

export function jsonParse(res: any) {
  return res?.data;
}

export const messageMap: { [key: number]: string } = {
  401: I18n.t('sdk_authentication_failed', {}, '登录失效'),
  403: I18n.t(
    'the_resource_you_requested_is_denied_access',
    {},
    '您所请求的资源被禁止访问',
  ),
  404: I18n.t(
    'the_resource_you_requested_does_not_exist',
    {},
    '您所请求的资源并不存在',
  ),
  405: I18n.t('illegal_requests_will_be_banned', {}, '非法请求会被禁止'),
  413: I18n.t(
    'the_amount_of_data_you_are_requesting_is_too_large',
    {},
    '您所请求的数据量太大',
  ),

  504: I18n.t('sorry__the_gateway_timed_out', {}, '很抱歉，网关超时了'),
};

function checkServerError(res: any) {
  const { status } = res;
  if (status >= 400 && status < 600) {
    const message =
      status < 500
        ? messageMap[status] ||
          `${I18n.t(
            'sorry__there_is_a_problem_with_the_program_({status})',
            { status: status },
            '很抱歉，程序出了一点问题（{status}）',
          )}`
        : messageMap[status] ||
          `${I18n.t(
            'server_deserted_({status})',
            { status: status },
            '服务器开小差了（{status}）',
          )}`;

    const error: EnhancedError = new Error(message);
    error.response = res;
    onErrorSubject.next({
      type: ERROR_TYPE.NET_ERROR,
      error: error,
    });
    return Promise.reject(error);
  }

  return res;
}

// Business status check
function checkServiceStatus(res: any) {
  if (!res?.config) {
    return res;
  }
  const { data = {}, config } = res || {};
  const { result, description, code } = data;
  if (result === 0 || code === 0 || config?.notCheckService) {
    return data;
  }
  const error: EnhancedError = new Error(description);
  error.data = data;
  error.response = data;
  return Promise.reject(error);
}

/**
 * Use standard fetch to create request
 * @param fetch Normalized fetch which provides same APIs
 * @param fetchOptions
 */
const createRequest =
  (
    fetch: Function,
    fetchOptions: {
      interceptors?: Interceptors;
      baseOptions?: any;
    } = {},
  ) =>
  (url: string, options: any = {}, pure = false): Promise<any> => {
    const { interceptors, baseOptions = {} } = fetchOptions;
    const requestInterceptor = interceptors?.request ?? ((_) => _);
    const responseInterceptor = interceptors?.response ?? ((_) => _);

    const { headers: baseHeaders = {}, ...otherBaseOptions } = baseOptions;
    const { headers: userHeaders = {}, ...otherUserOptions } = options;

    const opts = {
      ...otherBaseOptions,
      ...otherUserOptions,
      headers: {
        'Content-Type': 'application/json',
        lang: store.get('lang'),
        ...baseHeaders,
        ...userHeaders,
      },
    };

    const env = store.get('env');

    if (env === ENV.BOE && store.get('envTag')) {
      opts.headers['x-tt-env'] = store.get('envTag');
    }

    if (env === ENV.PPE && store.get('envTag')) {
      opts.headers['x-use-ppe'] = 1;
      opts.headers['x-tt-env'] = store.get('envTag');
    }

    //Compatibility satisfaction
    if (opts.body || opts.data) {
      opts.body = JSON.stringify(clearParams(opts.body || opts.data));
    }

    const fetchUrl =
      opts.origin || url.indexOf('/jarvis/') > -1
        ? url
        : getApiUrl(url, opts.params);

    if (pure) {
      return Promise.resolve(requestInterceptor(opts)).then((opts) =>
        fetch(fetchUrl, opts),
      );
    }

    return Promise.resolve(requestInterceptor(opts)).then((opts) =>
      fetch(fetchUrl, opts)
        .then(checkServerError)
        .then(checkServiceStatus)
        .then(responseInterceptor)
        .catch((error: HttpError) => {
          // 当前状态码是401并且不是轮训场景下的请求
          console.log(
            'error',
            error,
            error.response && error.response.description,
          );
          if (
            error &&
            error.message &&
            error.message.indexOf('401') > -1 &&
            !opts?.isTrain
          ) {
            onErrorSubject.next({
              type: ERROR_TYPE.NET_ERROR_401,
              error: error,
            });
          }
          throw error;
        }),
    );
  };

export type Interceptors = {
  response?: (res: any) => Promise<any>;
  request?: (config: any) => Promise<any>;
};

export default createRequest;
